import { queryClient } from 'lib/react-query';
import type { PackEntry } from 'types/shared';
import queryKeys from '../api/queryKeys';
import type { HypothesisCreativeEntry } from '../types/hypothesis';
import { SINGLE_AD_TYPE } from '../constants';

export const NO_HYPOTHESIS_TAG = 'No hypothesis';
export const NO_HYPOTHESIS_PACK_TAG = 'No hypothesis pack';
export const MIXED_PACK_TAG = 'Mixed pack';
export const MIXED_PACK_KEY = '__mixed_pack__';

export const isMixedPackTag = (tag: string): boolean =>
  tag.trim().toLowerCase() === MIXED_PACK_TAG.toLowerCase();

export type MixedPackEntry = Pick<PackEntry, 'tag' | 'isMixedPack'>;

export const isMixedPackEntry = (pack: MixedPackEntry): boolean =>
  pack.isMixedPack === true || isMixedPackTag(pack.tag);

export const packTagForHypothesis = (hypothesisLabel: string): string =>
  hypothesisLabel === NO_HYPOTHESIS_TAG ? NO_HYPOTHESIS_PACK_TAG : hypothesisLabel;

/** Maps a pack tag back to the hypothesis card key used in grouped views. */
export const packHypothesisKeyFromTag = (tag: string): string => {
  if (isMixedPackTag(tag)) return MIXED_PACK_KEY;
  if (tag === NO_HYPOTHESIS_PACK_TAG || !tag.trim()) return NO_HYPOTHESIS_TAG;
  return tag;
};

export const packHypothesisKeyFromPack = (pack: MixedPackEntry): string => {
  if (isMixedPackEntry(pack)) return MIXED_PACK_KEY;
  return packHypothesisKeyFromTag(pack.tag);
};

export const selectionSpansMultipleHypotheses = (
  selectedIds: readonly string[],
  idToHypothesis: Map<string, string>,
): boolean => {
  const hypotheses = new Set<string>();
  for (const id of selectedIds) {
    const hypothesis = idToHypothesis.get(id);
    if (hypothesis?.trim()) {
      hypotheses.add(hypothesis);
    } else if (idToHypothesis.has(id)) {
      hypotheses.add(NO_HYPOTHESIS_TAG);
    } else {
      hypotheses.add(`__unknown_hypothesis__:${id}`);
    }
    if (hypotheses.size > 1) return true;
  }
  return false;
};

export const getMixedPackCreativeIds = (
  packsWithCreatives: readonly Pick<PackEntry, 'tag' | 'creatives' | 'isMixedPack'>[],
): Set<string> => {
  const ids = new Set<string>();
  for (const pack of packsWithCreatives) {
    if (!isMixedPackEntry(pack)) continue;
    for (const id of pack.creatives) {
      if (typeof id === 'string') ids.add(id);
    }
  }
  return ids;
};

export const getHypothesisPackedCreativeIds = (
  packsWithCreatives: readonly Pick<PackEntry, 'tag' | 'creatives' | 'isMixedPack'>[],
): Set<string> => {
  const ids = new Set<string>();
  for (const pack of packsWithCreatives) {
    if (isMixedPackEntry(pack)) continue;
    for (const id of pack.creatives) {
      if (typeof id === 'string') ids.add(id);
    }
  }
  return ids;
};

export const buildPackEntry = (
  tag: string,
  creatives: string[],
  options?: { isMixedPack?: boolean },
): PackEntry => ({
  tag,
  adType: SINGLE_AD_TYPE,
  creatives,
  filterCreativeByName: [],
  visual_touch_ups: true,
  ...(options?.isMixedPack ? { isMixedPack: true } : {}),
});

export const removeCreativeIdsFromPacks = (
  packs: readonly PackEntry[],
  creativeIds: readonly string[],
): PackEntry[] => {
  const idsToRemove = new Set(creativeIds);
  return packs.map((pack) => ({
    ...pack,
    creatives: pack.creatives.filter(
      (id): id is string => typeof id === 'string' && !idsToRemove.has(id),
    ),
  }));
};

export const removeCreativeIdsFromHypothesisPacks = (
  packs: readonly PackEntry[],
  creativeIds: readonly string[],
): PackEntry[] => {
  const idsToRemove = new Set(creativeIds);
  return packs.map((pack) => {
    if (isMixedPackEntry(pack)) return pack;
    return {
      ...pack,
      creatives: pack.creatives.filter(
        (id): id is string => typeof id === 'string' && !idsToRemove.has(id),
      ),
    };
  });
};

export const creativeHypothesisKey = (
  creativeId: string,
  idToHypothesis: ReadonlyMap<string, string>,
): string => idToHypothesis.get(creativeId) ?? NO_HYPOTHESIS_TAG;

export const isSameHypothesisPackContext = (
  pack: MixedPackEntry,
  creativeHypothesis: string,
): boolean =>
  !isMixedPackEntry(pack) &&
  packHypothesisKeyFromPack(pack) === creativeHypothesis;

export const shouldHideCreativeFromUnpacked = (
  creative: Pick<HypothesisCreativeEntry, 'id' | 'hypothesis'>,
  packs: readonly PackEntry[],
): boolean => {
  const creativeKey = creative.hypothesis ?? NO_HYPOTHESIS_TAG;
  for (const pack of packs) {
    if (isMixedPackEntry(pack)) continue;
    if (!pack.creatives.includes(creative.id)) continue;
    if (packHypothesisKeyFromPack(pack) === creativeKey) {
      return true;
    }
  }
  return false;
};

export const removeCreativeIdsFromOwnedHypothesisPacks = (
  packs: readonly PackEntry[],
  creativeIds: readonly string[],
  idToHypothesis: ReadonlyMap<string, string>,
): PackEntry[] => {
  const ids = new Set(creativeIds);
  return packs.map((pack) => {
    if (isMixedPackEntry(pack)) return pack;
    const packKey = packHypothesisKeyFromPack(pack);
    return {
      ...pack,
      creatives: pack.creatives.filter((id) => {
        if (typeof id !== 'string' || !ids.has(id)) return true;
        const creativeKey = idToHypothesis.get(id) ?? NO_HYPOTHESIS_TAG;
        return packKey !== creativeKey;
      }),
    };
  });
};

export const getTestCreativesIdToHypothesis = (): Map<string, string> => {
  const cachedTestCreatives = queryClient
    .getQueryCache()
    .findAll({ queryKey: queryKeys.allTestCreatives })
    .flatMap((query) => query.state.data as HypothesisCreativeEntry[])
    .filter((c): c is HypothesisCreativeEntry => !!c);

  const idToHypothesis = new Map<string, string>();
  for (const creative of cachedTestCreatives) {
    const nextHypothesis = creative.hypothesis?.trim()
      ? creative.hypothesis
      : NO_HYPOTHESIS_TAG;
    const currentHypothesis = idToHypothesis.get(creative.id);
    if (!currentHypothesis) {
      idToHypothesis.set(creative.id, nextHypothesis);
      continue;
    }
    // Prefer a concrete hypothesis over "No hypothesis" when duplicates exist.
    if (
      currentHypothesis === NO_HYPOTHESIS_TAG &&
      nextHypothesis !== NO_HYPOTHESIS_TAG
    ) {
      idToHypothesis.set(creative.id, nextHypothesis);
    }
  }
  return idToHypothesis;
};

export const groupIdsByHypothesis = (
  selectedIds: readonly string[],
  idToHypothesis: Map<string, string>,
): Map<string, string[]> => {
  const grouped = new Map<string, string[]>();
  for (const id of selectedIds) {
    const hypothesis = idToHypothesis.get(id) ?? NO_HYPOTHESIS_TAG;
    const bucket = grouped.get(hypothesis);
    if (bucket) bucket.push(id);
    else grouped.set(hypothesis, [id]);
  }
  return grouped;
};

/** Splits ids into packCount groups; earlier packs get +1 when count does not divide evenly. */
export const splitIdsIntoPacks = (
  ids: readonly string[],
  packCount: number,
): string[][] => {
  if (packCount < 1 || packCount > ids.length) return [ids.slice()];

  const packs: string[][] = Array.from({ length: packCount }, () => []);
  let index = 0;
  const baseSize = Math.floor(ids.length / packCount);
  let extraPacks = ids.length % packCount;

  for (let packIdx = 0; packIdx < packCount; packIdx++) {
    const size = baseSize + (extraPacks > 0 ? 1 : 0);
    if (extraPacks > 0) extraPacks--;
    packs[packIdx] = ids.slice(index, index + size);
    index += size;
  }

  return packs;
};

export const defaultSplitPackCount = (creativeCount: number): number =>
  Math.min(2, creativeCount);

export const clampSplitPackCount = (
  value: number,
  creativeCount: number,
): number => Math.min(Math.max(1, value), creativeCount);

export const parseSplitPackCountInput = (
  rawValue: string,
  creativeCount: number,
): number => {
  const parsed = Number.parseInt(rawValue.trim(), 10);
  if (Number.isNaN(parsed)) {
    return defaultSplitPackCount(creativeCount);
  }
  return clampSplitPackCount(parsed, creativeCount);
};

export const isValidSplitPackCountInput = (
  rawValue: string,
  creativeCount: number,
): boolean => {
  const trimmed = rawValue.trim();
  if (!trimmed || !/^\d+$/.test(trimmed)) return false;
  const parsed = Number.parseInt(trimmed, 10);
  return parsed >= 1 && parsed <= creativeCount;
};
